/**
 * Composites call video tiles into an organized grid for browser Picture-in-Picture.
 * Avoids copying the messy live layout; builds a clean gallery frame instead.
 */

export type PipTile = {
    video: HTMLVideoElement;
    label: string;
    mirror: boolean;
};

const PAD = 16;
const GAP = 10;
const LABEL_H = 28;
const RADIUS = 12;
const TARGET_W = 1280;
const TARGET_H = 720;

function gridDims(count: number): { cols: number; rows: number } {
    if (count <= 1) return { cols: 1, rows: 1 };
    if (count === 2) return { cols: 2, rows: 1 };
    if (count === 3 || count === 4) return { cols: 2, rows: 2 };
    if (count <= 6) return { cols: 3, rows: 2 };
    if (count <= 9) return { cols: 3, rows: 3 };
    const cols = Math.ceil(Math.sqrt(count));
    return { cols, rows: Math.ceil(count / cols) };
}

function roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

function drawContain(
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    x: number,
    y: number,
    w: number,
    h: number,
    mirror: boolean
) {
    const vw = video.videoWidth || w;
    const vh = video.videoHeight || h;
    if (!vw || !vh) return;

    const scale = Math.min(w / vw, h / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    const dx = x + (w - dw) / 2;
    const dy = y + (h - dh) / 2;

    ctx.save();
    if (mirror) {
        ctx.translate(dx + dw, dy);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, dw, dh);
    } else {
        ctx.drawImage(video, dx, dy, dw, dh);
    }
    ctx.restore();
}

function resolveLabel(video: HTMLVideoElement): string {
    const root = video.closest(".fm-video") || video.parentElement;
    const nameEl = root?.querySelector(".co-name-label span:not(.co-you-tag)");
    const text = nameEl?.textContent?.trim();
    if (text) return text;
    if (root?.classList.contains("fm-video-local")) return "You";
    return "Participant";
}

export function collectPipTiles(container: HTMLElement): PipTile[] {
    const videos = Array.from(container.querySelectorAll("video")) as HTMLVideoElement[];
    return videos
        .filter((v) => v.readyState >= 2 && v.videoWidth > 0)
        .map((video) => ({
            video,
            label: resolveLabel(video),
            mirror: video.classList.contains("self-mirrored"),
        }));
}

export function paintPipFrame(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    tiles: PipTile[]
) {
    const W = canvas.width;
    const H = canvas.height;

    // Background
    ctx.fillStyle = "#0b0d12";
    ctx.fillRect(0, 0, W, H);

    if (tiles.length === 0) {
        ctx.fillStyle = "#64748b";
        ctx.font = "600 28px -apple-system, BlinkMacSystemFont, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Waiting for video…", W / 2, H / 2);
        return;
    }

    const { cols, rows } = gridDims(tiles.length);
    const cellW = (W - PAD * 2 - GAP * (cols - 1)) / cols;
    const cellH = (H - PAD * 2 - GAP * (rows - 1)) / rows;

    tiles.forEach((tile, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        // Center incomplete last row
        const itemsInRow =
            row === rows - 1 ? tiles.length - row * cols : cols;
        const rowOffset =
            itemsInRow < cols
                ? ((cols - itemsInRow) * (cellW + GAP)) / 2
                : 0;

        const x = PAD + rowOffset + col * (cellW + GAP);
        const y = PAD + row * (cellH + GAP);

        // Tile plate
        ctx.save();
        roundRect(ctx, x, y, cellW, cellH, RADIUS);
        ctx.fillStyle = "#11141c";
        ctx.fill();
        ctx.clip();

        // Video area above label strip
        const mediaH = Math.max(40, cellH - LABEL_H);
        drawContain(ctx, tile.video, x, y, cellW, mediaH, tile.mirror);

        // Label bar
        ctx.fillStyle = "rgba(8, 10, 16, 0.82)";
        ctx.fillRect(x, y + mediaH, cellW, LABEL_H);
        ctx.fillStyle = "#e2e8f0";
        ctx.font = "600 14px -apple-system, BlinkMacSystemFont, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        const label = tile.label.length > 28 ? `${tile.label.slice(0, 27)}…` : tile.label;
        ctx.fillText(label, x + 12, y + mediaH + LABEL_H / 2);

        ctx.restore();

        // Subtle border
        ctx.save();
        roundRect(ctx, x, y, cellW, cellH, RADIUS);
        ctx.strokeStyle = "rgba(255,255,255,0.10)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();
    });
}

export function createPipCanvas(): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.width = TARGET_W;
    canvas.height = TARGET_H;
    return canvas;
}
